/**
 * WebSocket transport.
 *
 * Every RustDesk message is exactly one binary WebSocket frame: unlike the TCP
 * transport there is no length prefix (see hbb_common's websocket.rs), so
 * framing is the socket's job and ours is only encryption.
 */
import { StreamCipher } from "./crypto.js";
export class WsTransport {
    #ws = null;
    #cipher = null;
    #queue = [];
    #waiters = [];
    #closed = false;
    #closeReason = "";
    url;
    constructor(url) {
        this.url = url;
    }
    get secured() {
        return this.#cipher !== null;
    }
    get closed() {
        return this.#closed;
    }
    get closeReason() {
        return this.#closeReason;
    }
    connect(timeoutMs = 15_000) {
        return new Promise((resolve, reject) => {
            let settled = false;
            const ws = new WebSocket(this.url);
            ws.binaryType = "arraybuffer";
            this.#ws = ws;
            const timer = setTimeout(() => {
                if (settled)
                    return;
                settled = true;
                try {
                    ws.close();
                }
                catch { /* already closing */ }
                reject(new Error("Connection timed out"));
            }, timeoutMs);
            ws.onopen = () => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(timer);
                resolve();
            };
            ws.onerror = () => {
                // Fires before onclose. Defer, so a close frame carrying a reason
                // (the server refusing with an explanation) wins over this generic
                // message.
                setTimeout(() => {
                    if (settled)
                        return;
                    settled = true;
                    clearTimeout(timer);
                    reject(new Error("Could not reach the server"));
                }, 20);
            };
            ws.onclose = (ev) => {
                this.#closed = true;
                // The server refuses with an application close code (4xxx) and a
                // human-readable reason; keep that verbatim.
                this.#closeReason = ev.reason || `closed (${ev.code})`;
                clearTimeout(timer);
                // Wake anyone blocked in recv() so they see the close.
                for (const w of this.#waiters.splice(0))
                    w(null);
                if (!settled) {
                    settled = true;
                    reject(new Error(this.#closeReason || "The connection was closed"));
                }
            };
            ws.onmessage = (ev) => {
                if (!(ev.data instanceof ArrayBuffer))
                    return;
                let frame = new Uint8Array(ev.data);
                if (this.#cipher) {
                    const plain = this.#cipher.open(frame);
                    if (!plain) {
                        // A frame that will not decrypt means the stream is desynchronised
                        // or tampered with; continuing would feed garbage to the parser.
                        this.close("decryption failed");
                        return;
                    }
                    frame = plain;
                }
                const waiter = this.#waiters.shift();
                if (waiter)
                    waiter(frame);
                else
                    this.#queue.push(frame);
            };
        });
    }
    /** Enables encryption for every subsequent frame in both directions. */
    setKey(key) {
        this.#cipher = new StreamCipher(key);
    }
    send(payload) {
        const ws = this.#ws;
        if (!ws || ws.readyState !== WebSocket.OPEN)
            return;
        ws.send(this.#cipher ? this.#cipher.seal(payload) : payload);
    }
    /** Resolves with the next frame, or null once the socket is closed. */
    recv(timeoutMs = 0) {
        const queued = this.#queue.shift();
        if (queued)
            return Promise.resolve(queued);
        if (this.#closed)
            return Promise.resolve(null);
        return new Promise((resolve) => {
            let done = false;
            const settle = (f) => {
                if (done)
                    return;
                done = true;
                resolve(f);
            };
            this.#waiters.push(settle);
            if (timeoutMs > 0)
                setTimeout(() => settle(null), timeoutMs);
        });
    }
    close(reason = "closed") {
        this.#closed = true;
        this.#closeReason = reason;
        try {
            this.#ws?.close(1000, reason.slice(0, 100));
        }
        catch {
            /* already closing */
        }
        for (const w of this.#waiters.splice(0))
            w(null);
    }
}
